import { HTMLAttributes } from 'react'

import CtaButton from '@/components/base/cta-button'
import Media from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { editorialRelatedRevealAnimation } from '@/components/sections/animations'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type EditorialRelatedProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    type?: 'dark' | 'light' | 'full_image'
    pretitle?: string
    title?: string
    media?: WPMedia
    text?: string
    link_1?: WPLink
    link_2?: WPLink
  }>

const EditorialRelated = ({
  type = 'light',
  pretitle,
  title,
  media,
  text,
  link_1,
  link_2,
  className,
  ...props
}: EditorialRelatedProps) => {
  return (
    <Motion animation={editorialRelatedRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} className={cn(className)} {...props}>
          <Spacer variant="xxl" />
          <div
            className={cn(
              'relative mx-[20px] p-spacing-xs md:p-spacing-sm',
              {
                'bg-surface-accent-dark': type === 'dark',
                'bg-surface-accent-light dark:bg-surface-accent-dark':
                  type === 'light',
                'bg-transparent': type === 'full_image'
              },
              'flex flex-col gap-y-spacing-md md:flex-row md:justify-between md:gap-y-spacing-lg'
            )}
          >
            <div
              className={cn(
                'relative z-10 flex flex-[1_1_auto] flex-col gap-y-spacing-md md:gap-y-spacing-lg'
              )}
            >
              {pretitle ? (
                <Typography
                  variant="large"
                  className={cn('condensed', {
                    'md:!text-text-primary-dark': type === 'full_image',
                    'text-text-primary-light': type === 'light',
                    'text-text-primary-dark': type === 'dark'
                  })}
                  data-motion-pretitle
                  component="p"
                >
                  {pretitle}
                </Typography>
              ) : null}

              {title ? (
                <Typography
                  variant="h3"
                  component="h2"
                  data-motion-title
                  className={cn('text-s-h3 md:text-s-h2', {
                    'md:!text-text-heading-dark': type === 'full_image',
                    'text-text-heading-light': type === 'light',
                    'text-text-heading-dark': type === 'dark'
                  })}
                >
                  {title}
                </Typography>
              ) : null}

              {text ? (
                <Typography
                  variant="h6"
                  paragraph
                  data-motion-text
                  className={cn({
                    'md:!text-text-primary-dark md:dark:!text-text-primary-dark':
                      type === 'full_image',
                    'text-text-primary-light': type === 'light',
                    'text-text-primary-dark': type === 'dark'
                  })}
                >
                  {text}
                </Typography>
              ) : null}

              <div
                className="flex flex-wrap gap-spacing-2xs place-self-start"
                data-motion-ctas
              >
                {link_1?.url ? (
                  <CtaButton
                    href={link_1.url}
                    target={link_1.target}
                    variant="primary"
                    typography="regular"
                    className={cn('min-w-[250px] md:min-w-0', {
                      dark: type === 'full_image' || type === 'dark'
                    })}
                    data-motion-button
                  >
                    {link_1.title}
                  </CtaButton>
                ) : null}

                {link_2?.url ? (
                  <CtaButton
                    href={link_2.url}
                    target={link_2.target}
                    variant="secondary"
                    typography="regular"
                    className={cn('min-w-[250px] md:min-w-0', {
                      dark: type === 'full_image' || type === 'dark'
                    })}
                  >
                    {link_2.title}
                  </CtaButton>
                ) : null}
              </div>
            </div>

            {media?.poster?.url ? (
              <div
                className={cn(
                  'relative z-0 block w-auto flex-[1_0_auto] justify-end md:flex',
                  {
                    'md:absolute md:inset-0 md:!aspect-auto':
                      type === 'full_image'
                  }
                )}
                style={{
                  aspectRatio: `${media?.poster.width} / ${media?.poster.height}`
                }}
                data-motion-media
              >
                <div
                  style={{
                    aspectRatio: `${media?.poster.width} / ${media?.poster.height}`
                  }}
                  className={cn('relative', {
                    'md:!aspect-auto md:h-full md:w-full md:bg-[black]':
                      type === 'full_image'
                  })}
                >
                  <Media
                    media={media}
                    className={cn('absolute left-0 top-0 h-full w-full', {
                      'md:opacity-60': type === 'full_image'
                    })}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <Spacer variant="xxl" />
        </Section>
      )}
    </Motion>
  )
}

export default EditorialRelated
